var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { BpgEventsService } from '../../bpg-events/bpg-events.service';
import { HoshinBudgetService } from '../../bpg-hoshin/hoshin-budget.service';
import { SelfService } from '../../bpg-self-services/self-services.service.ts';
import { classifyApiError } from './omnidashboard-error';
import { buildFiscalYearOptions } from './omnidashboard-fiscal-year';
import { createInitialOmnidashboardState, } from './omnidashboard-state.model';
import { OmnidashboardStorageService } from './omnidashboard-storage.service';
import { resolveOmniFilters, sanitizeFilterSelections } from '../filters/omnidashboard-filters';
import { applyMetricToAllCells, buildMatrixFromChapters, buildMatrixViewModel, createEmptyMatrixViewModel, } from '../matrix/omnidashboard-matrix-view';
import { buildMatrixGrid } from '../matrix/omnidashboard-matrix';
import { DEFAULT_OMNI_VIEW, getMetricLoader } from '../metrics/metric-registry';
/** Owns omnidashboard state, API orchestration, and matrix/metric recomputation. */
var OmnidashboardFacadeService = /** @class */ (function () {
    function OmnidashboardFacadeService(bpgEventsService, selfService, omniStorage, hoshinBudgetService) {
        this.bpgEventsService = bpgEventsService;
        this.selfService = selfService;
        this.omniStorage = omniStorage;
        this.hoshinBudgetService = hoshinBudgetService;
        var fy = buildFiscalYearOptions();
        this.state = createInitialOmnidashboardState(fy.options, fy.selectedFiscalYear);
    }
    OmnidashboardFacadeService.prototype.getState = function () {
        return this.state;
    };
    OmnidashboardFacadeService.prototype.initFiscalYearFromStorage = function () {
        var savedFy = this.omniStorage.getSelectedFiscalYear();
        if (savedFy) {
            this.state = __assign({}, this.state, { selectedFiscalYear: savedFy });
        }
    };
    OmnidashboardFacadeService.prototype.setActiveView = function (view) {
        this.state = __assign({}, this.state, { activeView: view });
        if (this.state.matrix.siteColumns.length > 0 || this.state.matrix.ergRows.length > 0) {
            this.rebuildViewModel(this.state.filters);
        }
    };
    OmnidashboardFacadeService.prototype.updateFilters = function (filters) {
        this.rebuildViewModel(filters);
    };
    OmnidashboardFacadeService.prototype.clearFilters = function () {
        this.rebuildViewModel({ selectedSiteNames: [], selectedErgNames: [] });
    };
    OmnidashboardFacadeService.prototype.persistFiscalYear = function () {
        this.omniStorage.setSelectedFiscalYear(this.state.selectedFiscalYear);
    };
    OmnidashboardFacadeService.prototype.loadMatrix = function (userId, roleId, onComplete) {
        var _this = this;
        this.persistFiscalYear();
        this.state = __assign({}, this.state, { isLoading: true, isEmptyResult: false, errors: { hasTimeoutError: false, hasApiError: false } });
        var dto = { userId: userId, roleId: roleId, fiscalYear: this.state.selectedFiscalYear };
        return this.bpgEventsService.getChapterDropDown(dto).pipe(tap(function (chapters) {
            if (!chapters || chapters.length === 0) {
                _this.state = __assign({}, _this.state, { isEmptyResult: true, chapters: [], filters: { selectedSiteNames: [], selectedErgNames: [] }, matrix: createEmptyMatrixViewModel(), loadedMetrics: new Set([DEFAULT_OMNI_VIEW]), filterAnnounceMessage: '', siteFilterTooltip: '', ergFilterTooltip: '' });
                return;
            }
            var built = buildMatrixFromChapters(chapters);
            var filters = { selectedSiteNames: [], selectedErgNames: [] };
            var matrix = buildMatrixViewModel(_this.state.activeView, filters, built.siteColumns, built.ergRows, built.matrixCellMap, built.matrixGrid);
            var resolvedFilters = resolveOmniFilters(filters, built.siteColumns, built.ergRows, matrix.visibleChapterCount);
            _this.state = __assign({}, _this.state, { chapters: chapters, filters: {
                    selectedSiteNames: resolvedFilters.selectedSiteNames,
                    selectedErgNames: resolvedFilters.selectedErgNames,
                }, matrix: matrix, loadedMetrics: new Set([DEFAULT_OMNI_VIEW]), filterAnnounceMessage: resolvedFilters.filterAnnounceMessage, siteFilterTooltip: resolvedFilters.siteFilterTooltip, ergFilterTooltip: resolvedFilters.ergFilterTooltip });
        }), catchError(function (error) {
            _this.state = __assign({}, _this.state, { errors: classifyApiError(error) });
            return throwError(function () { return error; });
        }), finalize(function () {
            _this.state = __assign({}, _this.state, { isLoading: false, isMetricLoading: false });
            if (onComplete) {
                onComplete();
            }
        }));
    };
    OmnidashboardFacadeService.prototype.loadMetric = function () {
        var _this = this;
        var view = this.state.activeView;
        if (this.state.loadedMetrics.has(view)) {
            // Metric data already on cells; only re-render for the active view or filters.
            this.rebuildViewModel(this.state.filters);
            return of(undefined);
        }
        // Show default metric placeholders until the loader resolves (e.g. ack N/A cells).
        this.rebuildViewModel(this.state.filters);
        var resolverFactory = this.getMetricResolver(view);
        if (!resolverFactory) {
            // chapter-status is seeded in buildMatrixCellMap; no API loader registered.
            return of(undefined);
        }
        this.state = __assign({}, this.state, { isMetricLoading: true, errors: __assign({}, this.state.errors, { hasApiError: false }) });
        return resolverFactory.pipe(tap(function (resolver) {
            var updatedMap = applyMetricToAllCells(_this.state.matrix.matrixCellMap, view, resolver);
            var loadedMetrics = new Set(_this.state.loadedMetrics);
            loadedMetrics.add(view);
            _this.state = __assign({}, _this.state, { loadedMetrics: loadedMetrics, errors: { hasTimeoutError: false, hasApiError: false } });
            _this.rebuildViewModel(_this.state.filters, updatedMap);
        }), map(function () { return undefined; }), catchError(function (error) {
            _this.state = __assign({}, _this.state, { errors: classifyApiError(error), isMetricLoading: false });
            return of(undefined);
        }), finalize(function () {
            _this.state = __assign({}, _this.state, { isMetricLoading: false });
        }));
    };
    OmnidashboardFacadeService.prototype.isFilterActive = function () {
        var _a = this.state, filters = _a.filters, matrix = _a.matrix;
        // Empty selection means "all"; filtering only when a strict subset is selected.
        var sitesFiltered = filters.selectedSiteNames.length > 0 &&
            filters.selectedSiteNames.length < matrix.siteColumns.length;
        var ergsFiltered = filters.selectedErgNames.length > 0 &&
            filters.selectedErgNames.length < matrix.ergRows.length;
        return sitesFiltered || ergsFiltered;
    };
    OmnidashboardFacadeService.prototype.hasNoVisibleMatrix = function () {
        var matrix = this.state.matrix;
        return matrix.ergRows.length > 0 && (matrix.visibleSiteColumns.length === 0 || matrix.visibleErgRows.length === 0);
    };
    OmnidashboardFacadeService.prototype.rebuildViewModel = function (filters, matrixCellMap) {
        var _a = this.state, matrix = _a.matrix, activeView = _a.activeView;
        var cellMap = matrixCellMap || matrix.matrixCellMap;
        // When metric data changes, rebuild the grid from the updated cell map.
        var matrixGrid = matrixCellMap
            ? buildMatrixGrid(matrix.ergRows, matrix.siteColumns, cellMap)
            : matrix.matrixGrid;
        var sanitized = sanitizeFilterSelections(filters, matrix.siteColumns, matrix.ergRows);
        var viewModel = buildMatrixViewModel(activeView, sanitized, matrix.siteColumns, matrix.ergRows, cellMap, matrixGrid);
        var resolvedFilters = resolveOmniFilters(sanitized, matrix.siteColumns, matrix.ergRows, viewModel.visibleChapterCount);
        this.state = __assign({}, this.state, { filters: {
                selectedSiteNames: resolvedFilters.selectedSiteNames,
                selectedErgNames: resolvedFilters.selectedErgNames,
            }, matrix: viewModel, filterAnnounceMessage: resolvedFilters.filterAnnounceMessage, siteFilterTooltip: resolvedFilters.siteFilterTooltip, ergFilterTooltip: resolvedFilters.ergFilterTooltip });
    };
    OmnidashboardFacadeService.prototype.getMetricResolver = function (view) {
        var loader = getMetricLoader(view);
        if (!loader) {
            return null;
        }
        return loader.load({
            selfService: this.selfService,
            hoshinBudgetService: this.hoshinBudgetService,
            selectedFiscalYear: this.state.selectedFiscalYear,
        });
    };
    return OmnidashboardFacadeService;
}());
export { OmnidashboardFacadeService };
